import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { useAuth } from "../../components/context/authContext";
import { apiFetchCurrentUserDetails } from "../../api/backend/user";
import {
  apiGetOfferSchedules,
  apiGetBrandAmbassadorEvents,
} from "../../api/backend/brandAmbassador";
import {
  SmallPlainInsetBtn,
  ImgTopBtn,
  GlassBtn,
} from "../../components/buttons";
import {
  MoneyBagIcon,
  EventCalendarIcon,
  EarnedIcon,
  IIcon,
  LineIntersectingCircleIcon,
} from "../../components/icons";
import AmbassadorRewardStatusRateCard from "../../components/ambassadorStatusCard/ambassadorRewardStatusRateCard";
import AmbassadorEarnings from "../../components/ambassadorEarnings/ambassadorEarnings";
import AmbassadorStandingHistory from "../../components/ambassadorStandingHistory/ambassadorStandingHistory";
import AmbassadorPerformance from "../../components/ambassadorPerformance/ambassadorPerformance";
import "./brandAmbassadorDashboard.scss";

const BrandAmbassadorDashboard = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { handleLogout } = useAuth();
  const [profileData, setProfileData] = useState(null);
  const [offerCount, setOfferCount] = useState(0);
  const [latestEventId, setLatestEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    const loadAll = async () => {
      try {
        const userResp = await apiFetchCurrentUserDetails(userId);
        if (!active) return;
        const user = userResp.data;
        setProfileData(user);

        const offersResp = await apiGetOfferSchedules(userId, user.role);
        if (!active) return;
        const offers = Array.isArray(offersResp.data.schedules)
          ? offersResp.data.schedules
          : [];
        setOfferCount(offers.length);

        const eventsResp = await apiGetBrandAmbassadorEvents(userId);
        if (!active) return;
        const events = eventsResp.data.events || [];
        if (events.length) {
          setLatestEventId(events[0].id);
        }
      } catch (e) {
        console.error(e);
        if (active) setError(e);
      } finally {
        if (active) setLoading(false);
      }
    };
    loadAll();
    return () => {
      active = false;
    };
  }, [userId]);

  const scrollWithOffset = (el, offset) => {
    const elementPosition = el.getBoundingClientRect().top;
    const scrollY = window.scrollY;
    window.scrollTo({
      top: elementPosition + scrollY - offset,
      behavior: "smooth",
    });
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error.message}</div>;
  if (!profileData) return <div>No profile found.</div>;

  const minimalUser = { id: profileData.id, role: profileData.role };
  const onLogoutClick = () => {
    handleLogout();
    navigate("/");
  };

  return (
    <div id="ambassadorDashboard" className="brand-ambassador-dashboard">
      <div className="top-ambassadordash-section">
        <p>Hi {profileData.username}!</p>
        <ImgTopBtn
          className="responsive-btn"
          image={<LineIntersectingCircleIcon width="25" height="25" />}
          imgTopBtnWidth="100%"
          imgTopBtnTextFontSize="1.3rem"
          imgTopBtnFontColor="#b03a2e"
          imgTopBtnText="Logout"
          onClick={onLogoutClick}
        />
        <HashLink
          to={`/offers-schedule-lists/${minimalUser.id}/offers#offerTop`}
          scroll={(el) => scrollWithOffset(el, 50)}
          style={{ textDecoration: "none" }}
        >
          <div className="offerNotifications">
            {offerCount > 0 && (
              <span className="offers-badge">{offerCount}</span>
            )}
            <SmallPlainInsetBtn
              className="responsive-btn"
              image={
                <MoneyBagIcon
                  height="15px"
                  width="15px"
                  fillColor="var(--color-bloodred)"
                  style={{ marginRight: "3px" }}
                />
              }
              text="Offers"
              width="100%"
              style={{ fontSize: "1rem" }}
            />
          </div>
        </HashLink>
        <HashLink
          to={`/staff/${minimalUser.id}/schedule#scheduleTop`}
          scroll={(el) => scrollWithOffset(el, 50)}
          style={{ textDecoration: "none", flex: "1 1 120px" }}
        >
          <SmallPlainInsetBtn
            className="responsive-btn"
            image={
              <EventCalendarIcon
                height="20px"
                width="20px"
                fillColor="var(--color-bloodred)"
                style={{ marginRight: "3px" }}
              />
            }
            text="Schedule"
            width="100%"
            style={{ fontSize: "1rem" }}
          />
        </HashLink>

        <HashLink
          to={`/accounts-receivables-dashboard/${minimalUser.id}/accounts-receivables#acctReceivablesTop`}
          state={{ role: minimalUser.role }}
          scroll={(el) => scrollWithOffset(el, 50)}
          style={{ textDecoration: "none", flex: "1 1 120px" }}
        >
          <ImgTopBtn
            className="responsive-btn"
            image={
              <EarnedIcon
                height="20px"
                width="20px"
                style={{ paddingRight: "5px" }}
              />
            }
            imgTopBtnText="Accounts Payable Transaction History"
            imgTopBtnWidth="100%"
            imgTopBtnFontColor="var(--color-bloodred)"
            position="relative"
          />
        </HashLink>
      </div>

      <section className="ambassadorRewardsRow">
        <AmbassadorRewardStatusRateCard
          role={minimalUser.role}
          currentUserId={minimalUser.id}
        />
        <AmbassadorEarnings />
      </section>

      <section className="standing-section">
        <AmbassadorStandingHistory
          role={minimalUser.role}
          currentMemberId={minimalUser}
          brandAmbassadorEventId={latestEventId}
        />
        <div className="standing-btn-wrapper">
          <HashLink
            to={`/ambassador-dashboard/${minimalUser.id}/standing-metrics#ambassador-standing-metrics-top`}
            scroll={(el) => scrollWithOffset(el, 50)}
            style={{ flex: "1 1 120px", textDecoration: "none" }}
          >
            <GlassBtn
              className="glass-btn responsive-btn"
              glassBtnImage={
                <IIcon
                  height="15px"
                  width="15px"
                  fillColor="var(--color-yellow)"
                />
              }
              glassBtnText="See Standing Metrics"
              glassBtnWidth="400px"
              glassBtnHeight="30px"
              glassBtnTextFontSize="1rem"
              glassBtnFontWeight="900"
              glassBtnFontColor="var(--color-yellow)"
              glassBtnTextDecorationColor="var(--color-yellow)"
              /* ← ADD THIS: */
              glassHoverStyle={{
                height: "60px",
              }}
              hi2Style={{ transform: "rotate(90deg) translate(40px, 200px)" }}
              hi3Style={{ transform: "rotate(90deg) translate(40px, -200px)" }}
            />
          </HashLink>
        </div>
      </section>
      <section className="performance-section" style={{ marginBottom: "2rem" }}>
        <AmbassadorPerformance
          role={minimalUser.role}
          currentUserId={minimalUser.id}
        />
      </section>
    </div>
  );
};

export default BrandAmbassadorDashboard;
